import { ReactNode } from 'react';

import { RangeEnum } from '../../model/range.enum';
import {
  BacktesterConfigSummary,
  BacktesterParamsPayload,
  BacktesterRunRequest,
  BacktesterRunResult,
  BacktesterStrategyData,
} from './api/backtester-config-api.types';

export interface BacktestGlobalConfig {
  token: string;
  backtestPeriod: { start: Date | null; end: Date | null };
  backtestPeriodRange?: RangeEnum;
  frequency: number | null;
}

export interface BacktestHeaderErrors {
  token?: string;
  backtestPeriod?: string;
  frequency?: string;
}

export type BacktestHeaderField = keyof BacktestHeaderErrors;

export type BacktestViewMode = 'form' | 'result';

export type BacktestExecutionStatus = 'idle' | 'loading' | 'success';

export type BacktestResultTabKey =
  | 'overview'
  | 'market-risk'
  | 'market-data'
  | 'transaction-log'
  | 'summary'
  | 'specification'
  | 'request';

export interface BacktestExecutionState {
  viewMode: BacktestViewMode;
  status: BacktestExecutionStatus;
  executedAt: string | null;
  rawResult: BacktesterRunResult | null;
  strategyData: BacktesterStrategyData | null;
  strategyNames: string[];
  activeResultTab: BacktestResultTabKey;
  // Ids of strategies currently running via a per-variant Run (the Specification
  // card's Run action). Deliberately separate from `status`: a per-variant run
  // must NOT flip the study-wide loading state, which swaps the whole view to
  // the full-run loading screen and would clobber sibling variants' results.
  runningStrategyIds: string[];
  // The raw JSON-RPC request last sent over the wire for this tab (full study
  // or per-variant run) — surfaced verbatim on the Request tab for debugging
  // exactly what the engine received. Null until the study has been run once.
  lastRunRequest: BacktesterRunRequest | null;
}

export interface StrategyTab {
  id: string;
  label: string;
  hasError?: boolean;
}

export interface BacktestTab {
  id: string;
  label: string;
  globalConfig: BacktestGlobalConfig;
  headerErrors: BacktestHeaderErrors;
  strategies: StrategyTab[];
  activeStrategyId: string | null;
  execution: BacktestExecutionState;
}

export interface InstrumentRow {
  tenor: string;
  notional: string;
  deltaOrStrike: string;
}

export interface AccountInformationFormData {
  token: string;
  tokenBalance: string;
  usdBalance: string;
  includeInitialPerpetual: boolean;
  initialPerpetualBalance: string;
}

export interface ExecutionFormData {
  executionTime: string;
  whenExecutionWeekdays: boolean;
  whenExecutionWeekdaysDays: string[];
  whenExecutionWeekdaysTime: string;
  whenExecutionAverageIn: boolean;
  whenExecutionAverageInIncludeWeekends: boolean;
  optionsNotionalScaled: boolean;
  notionalScalingType: string;
  includePerpetualSpread: boolean;
  perpetualSpread: string;
  includeVolSpread: boolean;
  volSpread: string;
  includePerpetualFee: boolean;
  perpetualFee: string;
  includeOptionsFee: boolean;
  optionsFee: string;
  convertPerpCashflowToUsd: boolean;
  optionsSettleInUsd: boolean;
  convertFundingCashflowsToUsd: boolean;
  closeStructureOnFirstExpiry: boolean;
  executeTradesOnlyOnce: boolean;
  useExchangeStrikes: boolean;
  exchangeTimeProjection: string;
  exchangeStrikeProjection: string;
}

export interface HedgingFormData {
  frequency: string;
  includeFundingRates: boolean;
  deltaThreshold: boolean;
  deltaThresholdValue: string;
  deltaThresholdUpper: boolean;
  deltaThresholdUpperValue: string;
  deltaThresholdLower: boolean;
  deltaThresholdLowerValue: string;
  deltaTarget: boolean;
  deltaTargetValue: string;
  includeTokenBalanceInDelta: boolean;
}

export interface PortfolioFormData {
  instruments: InstrumentRow[];
}

export interface EntryConditionsFormData {
  enabled: boolean;
  category: string;
  token: string;
  instrument: string;
  tenor: string;
  comparison: string;
  value: string;
}

export interface ExitConditionsFormData {
  enabled: boolean;
  category: string;
  structurePvMaxIntrinsic: string;
  structurePvPremium: string;
  instrumentPvBase: string;
  timeValueBase: string;
  instrumentDeltaBaseQuote: string;
  strikeVol: string;
  timeToExpiry: string;
  spotMoveSinceInception: string;
  absoluteSpotMoveSinceInception: string;
}

export interface RollPositionsFormData {
  enabled: boolean;
  rollCondition: string;
  comparison: string;
  value: string;
  rollType: string;
}

export interface StrategyFormData {
  name: string;
  accountInformation: AccountInformationFormData;
  execution: ExecutionFormData;
  hedging: HedgingFormData;
  portfolio: PortfolioFormData;
  entryConditions: EntryConditionsFormData;
  exitConditions: ExitConditionsFormData;
  rollPositions: RollPositionsFormData;
}

export interface StrategyTemplate {
  id: string;
  label: string;
  sentiment: 'bullish' | 'bearish' | 'neutral';
  defaults: Partial<StrategyFormData>;
}

export type BacktestSubmitPayload = BacktesterParamsPayload;

export interface BacktestHeaderRegistryEntry {
  trigger: () => Promise<boolean>;
}

export interface StrategyFormRegistryEntry {
  backtestId: string;
  getValues: () => StrategyFormData;
  trigger: () => Promise<boolean>;
  setNameError: (message: string) => void;
  clearNameError: () => void;
}

export const DEFAULT_STRATEGY_FORM_DATA: StrategyFormData = {
  name: '',
  accountInformation: {
    token: 'BTC',
    tokenBalance: '',
    usdBalance: '',
    includeInitialPerpetual: false,
    initialPerpetualBalance: '',
  },
  execution: {
    executionTime: '',
    whenExecutionWeekdays: true,
    whenExecutionWeekdaysDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    whenExecutionWeekdaysTime: '',
    whenExecutionAverageIn: false,
    whenExecutionAverageInIncludeWeekends: false,
    optionsNotionalScaled: false,
    notionalScalingType: '',
    includePerpetualSpread: false,
    perpetualSpread: '',
    includeVolSpread: false,
    volSpread: '',
    includePerpetualFee: false,
    perpetualFee: '',
    includeOptionsFee: false,
    optionsFee: '',
    convertPerpCashflowToUsd: false,
    optionsSettleInUsd: false,
    convertFundingCashflowsToUsd: false,
    closeStructureOnFirstExpiry: false,
    executeTradesOnlyOnce: false,
    useExchangeStrikes: false,
    exchangeTimeProjection: 'weighted_time',
    exchangeStrikeProjection: 'weighted_strike',
  },
  hedging: {
    frequency: '',
    includeFundingRates: true,
    deltaThreshold: false,
    deltaThresholdValue: '',
    deltaThresholdUpper: false,
    deltaThresholdUpperValue: '',
    deltaThresholdLower: false,
    deltaThresholdLowerValue: '',
    deltaTarget: false,
    deltaTargetValue: '',
    includeTokenBalanceInDelta: false,
  },
  portfolio: {
    instruments: [{ tenor: '', notional: '', deltaOrStrike: '' }],
  },
  entryConditions: {
    enabled: false,
    category: 'market',
    token: 'BTC',
    instrument: '',
    tenor: '',
    comparison: 'gt',
    value: '',
  },
  exitConditions: {
    enabled: false,
    category: 'structure',
    structurePvMaxIntrinsic: '',
    structurePvPremium: '',
    instrumentPvBase: '',
    timeValueBase: '',
    instrumentDeltaBaseQuote: '',
    strikeVol: '',
    timeToExpiry: '',
    spotMoveSinceInception: '',
    absoluteSpotMoveSinceInception: '',
  },
  rollPositions: {
    enabled: false,
    rollCondition: 'short_options_delta',
    comparison: 'gt',
    value: '',
    rollType: '',
  },
};

export const createDefaultGlobalConfig = (): BacktestGlobalConfig => {
  const end = new Date();
  const start = new Date(Date.UTC(2024, 0, 1));

  return {
    token: 'BTC',
    backtestPeriod: { start, end },
    frequency: 15,
  };
};

export const DEFAULT_GLOBAL_CONFIG: BacktestGlobalConfig = createDefaultGlobalConfig();

export const DEFAULT_BACKTEST_EXECUTION_STATE: BacktestExecutionState = {
  viewMode: 'form',
  status: 'idle',
  executedAt: null,
  rawResult: null,
  strategyData: null,
  strategyNames: [],
  // A not-yet-run backtest (new / duplicated) opens on Specification — the
  // config form — inside the shell. After a run, runBacktest switches this to
  // 'overview' (the Performance tab).
  activeResultTab: 'specification',
  runningStrategyIds: [],
  lastRunRequest: null,
};

export interface IBacktestingProvider {
  backtestTabs: BacktestTab[];
  activeBacktestId: string | null;

  setActiveBacktestId: (id: string) => void;
  addBacktestTab: () => void;
  renameBacktestTab: (id: string, newLabel: string) => void;
  duplicateBacktestTab: (id: string) => void;
  deleteBacktestTab: (id: string) => void;

  updateGlobalConfig: (backtestId: string, config: Partial<BacktestGlobalConfig>) => void;

  addStrategy: (backtestId: string, label?: string, defaults?: Partial<StrategyFormData>) => void;
  duplicateStrategy: (backtestId: string, strategyId: string, data: StrategyFormData) => void;
  renameStrategy: (backtestId: string, strategyId: string, newLabel: string) => void;
  deleteStrategy: (backtestId: string, strategyId: string) => void;
  clearAllStrategiesForBacktest: (backtestId: string) => void;
  setActiveStrategy: (backtestId: string, strategyId: string) => void;
  updateStrategyDraft: (backtestId: string, strategyId: string, draft: StrategyFormData) => void;
  syncStrategyLabel: (backtestId: string, strategyId: string, name: string) => void;

  addStrategyFromTemplate: (template: StrategyTemplate) => void;
  // Reset the single working backtest to a fresh unsaved draft (blank, or
  // seeded from a template). The sidebar is the persistent library; there is
  // only ever one working copy.
  startNewBacktest: (template?: StrategyTemplate) => void;
  listSavedConfigs: (page?: number, pageSize?: number) => void;
  loadSavedConfig: (name: string) => void;
  deleteSavedConfig: (name: string) => void;
  renameSavedConfig: (oldName: string, newName: string) => void;
  duplicateSavedConfig: (sourceName: string, newName: string) => void;
  saveBacktestConfig: (name?: string) => void;
  updateBacktestConfig: () => void;
  savedConfigs: BacktesterConfigSummary[] | null;
  isListingSavedConfigs: boolean;
  isLoadingSavedConfig: boolean;
  isDeletingSavedConfig: boolean;
  isSavingConfig: boolean;
  deletedSavedConfigName: string | null;
  activeSavedConfigName: string | null;

  getActiveBacktest: () => BacktestTab | undefined;
  getStrategyDefaults: (backtestId: string, strategyId: string) => Partial<StrategyFormData>;
  registerBacktestHeader: (backtestId: string, entry: BacktestHeaderRegistryEntry) => void;
  unregisterBacktestHeader: (backtestId: string) => void;
  registerStrategyForm: (strategyId: string, entry: StrategyFormRegistryEntry) => void;
  unregisterStrategyForm: (strategyId: string) => void;
  setBacktestHeaderErrors: (backtestId: string, errors: BacktestHeaderErrors) => void;
  clearBacktestHeaderError: (backtestId: string, field: BacktestHeaderField) => void;
  showBacktestForm: (backtestId: string) => void;
  setActiveBacktestResultTab: (backtestId: string, tab: BacktestResultTabKey) => void;

  runBacktest: () => Promise<boolean>;
  // Run a SINGLE variant (the Specification card's per-row Run). Runs one
  // stratlet, merges just that variant's result into the study's result set
  // (siblings preserved), and auto-saves the full study.
  runStrategy: (backtestId: string, strategyId: string) => Promise<boolean>;
}

export interface IBacktestingProviderProps {
  children: ReactNode;
}
